import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { FormManager } from "../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { baseAuthApi } from "../../../../../core/service/baseApi";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import {
    useEmvGlobalConfigurationLoadQuery,
    useEmvGlobalConfigurationUpdateMutation,
} from "../../services/server/EmvGlobalConfigurationApi";
import type { EmvGlobalConfigurationEntity } from "../../types/EmvGlobalConfigurationEntity";

export const EmvGlobalConfigurationEditForm: FC<FormProps> = ({ runSubmit }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("emv-global-configuration");

    const [formData, setFormData] = useState<EmvGlobalConfigurationEntity>({} as EmvGlobalConfigurationEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useEmvGlobalConfigurationLoadQuery();

    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useEmvGlobalConfigurationUpdateMutation();
    const dispatch = useDispatch();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formUpdateSuccessNoId", {
                            resource: "EMV Global Configuration",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                dispatch(baseAuthApi.util.invalidateTags([ResourceName.EMV_GLOBAL_CONFIGURATIONS]));
            }
        },
        [saveFormData, formData, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<EmvGlobalConfigurationEntity>
                    name="EmvGlobalConfigurationEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.DEFAULT}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
